// AGC: useKeyboardScroll — reusable hook for mobile keyboard-aware input scrolling
// AGC: When an input field gets focus on Android, the keyboard covers the bottom
// AGC: of the viewport. This hook scrolls the input into view so it (and the
// AGC: action button below it) stay visible above the keyboard.
//
// AGC: Usage:
//   const { onFocus } = useKeyboardScroll()
//   <input onFocus={onFocus} ... />
//   <button>Continuar</button>  ← stays visible because input is centered

import { useCallback } from 'react'

interface UseKeyboardScrollOptions {
  /** AGC: How to align the input in the viewport. Default: 'center' */
  block?: ScrollLogicalPosition
  /** AGC: Delay (ms) before scrolling — gives the keyboard time to open. Default: 300 */
  delay?: number
}

/**
 * AGC: Returns an onFocus handler that scrolls the focused input into view
 * AGC: after the Android keyboard finishes opening. Place an inline action
 * AGC: button right below the input so both stay visible above the keyboard.
 */
export function useKeyboardScroll(options?: UseKeyboardScrollOptions) {
  const block = options?.block ?? 'center'
  const delay = options?.delay ?? 300

  const onFocus = useCallback((e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target
    setTimeout(() => {
      target.scrollIntoView({ behavior: 'smooth', block })
    }, delay)
  }, [block, delay])

  return { onFocus }
}

export default useKeyboardScroll
